import { Trash2, X } from 'lucide-react'
import type { AudioClip } from '@shared/types'
import { useEditorStore } from '../../../store/editorStore'
import { CheckboxField, SliderField } from '../../common/Field'
import { getTotalDurationSec } from '../../../lib/timelineMath'
import { assetDisplayName } from '../../../lib/assetName'
import { useAudioLibrary } from './TabAudio'

/**
 * task spec §3-4: Audio 選択時のインスペクタ。旧 TabAudio.tsx の「クリップごとの
 * カード」1 件分を単体編集用に切り出したもの（gainDb / fadeInSec / fadeOutSec /
 * inSec / outSec）。Selection 型には既に `{kind:'audio'; id}` があるので
 * (editorStore.ts SelectionItem)、「無ければ音タブで代替」の分岐は不要だった —
 * Timeline の AudioLane クリックがこの kind で select() を呼べば即座に開く。
 */
export function AudioInspector({ audioId }: { audioId: string }) {
  const composition = useEditorStore((s) => s.composition)
  const assets = useEditorStore((s) => s.assets)
  const updateAudio = useEditorStore((s) => s.updateAudio)
  const removeAudio = useEditorStore((s) => s.removeAudio)
  const select = useEditorStore((s) => s.select)
  // P10-2 担当B §3 item 2: builtin 表示名は GET /api/audio/library の一覧から
  // 解決する（見つからなければ id をそのまま表示 — task spec の指定どおり）。
  // TabAudio.tsx の同フックを再利用（モジュールキャッシュ共有・二重fetch無し）。
  const { library } = useAudioLibrary()

  const clip = composition?.tracks.audio.find((a) => a.id === audioId)
  if (!composition || !clip) return null

  const asset = clip.assetId ? assets[clip.assetId] : null
  const sourceLabel = clip.builtinId
    ? (library?.items.find((i) => i.id === clip.builtinId)?.title ?? clip.builtinId)
    : (asset ? assetDisplayName(asset) : '未設定')
  const totalSec = getTotalDurationSec(composition)
  const clipId = clip.id

  function patch(p: Partial<AudioClip>) {
    updateAudio(clipId, p)
  }

  return (
    <div className="p-3 space-y-4" data-testid="audio-inspector">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold">{clip.kind === 'bgm' ? 'BGM' : 'SFX'}</div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => removeAudio(clip.id)}
            className="text-text-faint hover:text-accent-red p-1"
            title="削除"
            data-testid="audio-inspector-delete"
          >
            <Trash2 size={13} />
          </button>
          <button
            onClick={() => select(null)}
            className="text-text-faint hover:text-text p-1"
            title="選択解除"
            data-testid="audio-inspector-close"
          >
            <X size={14} />
          </button>
        </div>
      </div>
      <div className="text-xs text-text-muted truncate">{sourceLabel}</div>

      <SliderField
        label="音量"
        value={clip.gainDb}
        min={-40}
        max={6}
        step={1}
        unit="dB"
        onChange={(gainDb) => patch({ gainDb })}
      />
      {/* reviewer minor (InspectorFrame と同件): grid-cols-2 で SliderField を
          横並びにすると右ペイン最小幅 280px で数値入力が欠ける。縦積みにする。 */}
      <div className="space-y-3">
        <SliderField
          label="フェードイン"
          value={clip.fadeInSec}
          min={0}
          max={5}
          step={0.1}
          unit="s"
          onChange={(fadeInSec) => patch({ fadeInSec })}
        />
        <SliderField
          label="フェードアウト"
          value={clip.fadeOutSec}
          min={0}
          max={5}
          step={0.1}
          unit="s"
          onChange={(fadeOutSec) => patch({ fadeOutSec })}
        />
      </div>

      <SliderField
        label="ソースIN"
        value={clip.inSec}
        min={0}
        max={Math.max(clip.inSec + 60, clip.outSec ?? clip.inSec + 60)}
        step={0.1}
        unit="s"
        onChange={(inSec) => patch({ inSec })}
      />
      <CheckboxField
        label="末尾まで再生"
        checked={clip.outSec === null}
        onChange={(toEnd) => patch({ outSec: toEnd ? null : Math.max(clip.inSec + 0.5, totalSec) })}
      />
      {clip.outSec !== null && (
        <SliderField
          label="ソースOUT"
          value={clip.outSec}
          min={clip.inSec}
          max={Math.max(clip.inSec + 60, clip.outSec)}
          step={0.1}
          unit="s"
          onChange={(outSec) => patch({ outSec })}
        />
      )}
    </div>
  )
}
